import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon, type IconKey } from "@core/assets";
import { cn } from "@core/utils";
import type { QuestionMarkerState } from "./question-state";

const MARKER_ICON: Record<QuestionMarkerState, IconKey> = {
  upcoming: "circle-dashed",
  "in-progress": "circle-dot",
  answered: "circle-check",
  warning: "alert-triangle",
  error: "circle-alert",
};

/**
 * ⚠️ "En cours" (in-progress) is DELIBERATELY the same neutral gray as "À venir", not brand-tinted.
 * Several questions are open at once on one step (one per section), and painting all of them
 * brand/600 would repeat "you are here" that many times and drain the accent from the one control
 * that actually needs it — « Suivant ». Verbatim from the Figma component's own documentation.
 */
const MARKER_TONE: Record<QuestionMarkerState, string> = {
  upcoming: "text-(--text-tertiary)",
  "in-progress": "text-(--action-secondary-fg)",
  answered: "text-menthe-600",
  warning: "text-(--feedback-warning-icon)",
  error: "text-(--feedback-error-icon)",
};

interface DossierQuestionRowProps {
  label: string;
  /** Truncated to one line by CSS — only ever shown collapsed, and only when answered. */
  preview: string | null;
  state: QuestionMarkerState;
  isOpen: boolean;
  /**
   * False while `state === "error"` — a question with a hard input error can never collapse
   * (US-E3-07: the problem stays visible until it's fixed), so its marker button is inert rather
   * than hidden — hiding it would silently change the row's layout between states.
   */
  isCollapsible: boolean;
  onToggle: () => void;
  /** The real, rendered `DossierField` (or `TeamMembersPanel`/consent field) — never re-rendered
   * twice, this is the one and only place the actual control lives. */
  children: ReactNode;
}

/**
 * One collapsible question (Figma 679:28260 "Portail/Question — repliée (bureau)" + its "Ouverte"
 * sibling, and 678:28178 "Portail/Marqueur — question (bureau)" for the shared gutter marker).
 *
 * Progressive disclosure as PACING, never a barrier (US-E3-01 applied at the question level): a
 * collapsed row is clickable immediately regardless of its position — nothing about being "ahead"
 * blocks opening it early. There is no separate "Modifier" affordance; the marker/chevron IS the
 * whole control.
 */
export function DossierQuestionRow({
  label,
  preview,
  state,
  isOpen,
  isCollapsible,
  onToggle,
  children,
}: DossierQuestionRowProps) {
  const { t } = useTranslation("acquire");

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={false}
        className="flex min-h-11 w-full items-center gap-sm rounded-md px-xs py-sm text-left outline-none transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset"
      >
        <span aria-hidden className={cn("shrink-0", MARKER_TONE[state])}>
          <Icon name={MARKER_ICON[state]} size={20} />
        </span>
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-sm-medium",
            state === "upcoming" ? "text-(--text-tertiary)" : "text-(--text-primary)"
          )}
        >
          {label}
        </span>
        {preview ? (
          <span className="max-w-50 shrink-0 truncate text-sm-regular text-(--text-tertiary)">
            {preview}
          </span>
        ) : null}
        <Icon
          name="chevron-down"
          size={16}
          className="shrink-0 text-(--text-tertiary)"
        />
      </button>
    );
  }

  return (
    <div className="flex items-start gap-sm">
      <button
        type="button"
        onClick={isCollapsible ? onToggle : undefined}
        disabled={!isCollapsible}
        aria-expanded={true}
        aria-label={t("dossier.question.collapse", { label })}
        className={cn(
          "mt-3 flex shrink-0 items-center justify-center rounded-full outline-none",
          isCollapsible
            ? "cursor-pointer hover:opacity-80 focus-visible:ring-2 focus-visible:ring-focus-ring"
            : "cursor-not-allowed",
          MARKER_TONE[state]
        )}
      >
        <Icon name={MARKER_ICON[state]} size={20} />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
